import { useAuth } from "@dl/web-core-js/auth";
import { Button, cn, Dialog } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, Warehouse } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StockBalanceClient, type PartDTO } from "../../api/manufacturing";
import { StockAdjustmentForm } from "./StockAdjustmentForm";
import { StockMovementsGrid } from "./StockMovementsGrid";

/**
 * One part's balances, per warehouse.
 *
 * <p>Shared by the page header and the Stock tab under one query key, so both
 * read one request, and an adjustment or a reversal (which invalidate every
 * stock-balance query) refreshes both.</p>
 */
export function usePartBalances(partId: number) {
  const { http } = useAuth();
  const balances = useMemo(() => new StockBalanceClient(http), [http]);
  const query = useQuery({
    queryKey: ["manufacturing", "stock-balance", "part", partId],
    queryFn: () => balances.find({ page: 0, size: 50, filters: { part_id: [String(partId)] }, sort: ["warehouse_code,asc"] }),
  });
  const all = query.data?.content ?? [];
  // A warehouse at zero says nothing about where the part is.
  const rows = all.filter((row) => Number(row.quantity) !== 0);
  const total = rows.reduce((sum, row) => sum + Number(row.quantity), 0);
  return { ...query, rows, total, moved: all.some((row) => row.movement_count > 0) };
}

/** Stock quantities with thousands separators, in the interface's language. */
function useQuantityFormat() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const format = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 4 });
    return (value: number) => format.format(value);
  }, [i18n.language]);
}

/**
 * On hand, in the page header: "MEGA 7,384 · SERV -8 · total 7,376 each".
 * The total only when there is more than one warehouse to add up; a negative
 * balance in the risk colour, as on the Stock tab.
 */
export function PartStockHeadline({ partId, unitCode }: { partId: number; unitCode: string | null }) {
  const { t } = useTranslation();
  const format = useQuantityFormat();
  const { rows, total, isLoading, isError } = usePartBalances(partId);
  if (isLoading || isError) return null;
  const unit = unitCode ? ` ${unitCode}` : "";
  return (
    // An icon rather than the words "On hand": the bar is shared with the
    // title, which must stay readable.
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-surface-sunk px-2 py-0.5 text-[12px] tabular-nums text-ink"
      title={t("stock.on-hand", "On hand")}
      aria-label={t("stock.on-hand", "On hand")}
    >
      <Warehouse className="size-3.5 text-ink-muted" aria-hidden />
      {rows.length === 0 ? (
        <span className="font-semibold">
          {format(0)}
          {unit}
        </span>
      ) : (
        <>
          {rows.map((row, index) => (
            <span key={row.warehouse_id} className="inline-flex items-center gap-1">
              {index > 0 && <span className="text-ink-faint">·</span>}
              <span className="text-ink-muted">{row.warehouse_code}</span>
              <span className={cn("font-semibold", Number(row.quantity) < 0 && "text-risk")}>{format(Number(row.quantity))}</span>
            </span>
          ))}
          {rows.length > 1 && (
            <span className="inline-flex items-center gap-1">
              <span className="text-ink-faint">·</span>
              <span className="text-ink-muted">{t("stock.total.label", "total")}</span>
              <span className={cn("font-semibold", total < 0 && "text-risk")}>{format(total)}</span>
            </span>
          )}
          {unit !== "" && <span className="text-ink-muted">{unitCode}</span>}
        </>
      )}
    </span>
  );
}

/**
 * Where this part stands, per warehouse, and every movement that put it there.
 *
 * <p>A negative balance is shown as what it is — a finding, not an error. At
 * MEGA 1,938 parts are negative on SIGM's history; the count at go-live
 * corrects them, and until then hiding the sign would hide the problem.</p>
 */
export function PartStockTab({ part, unitCode }: { part: PartDTO; unitCode: string | null }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const [adjusting, setAdjusting] = useState(false);
  const format = useQuantityFormat();
  const { rows, total, moved, isLoading, isError } = usePartBalances(part.id);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-3 lg:px-6">
        {isLoading ? (
          <span className="text-[12px] text-ink-muted">{t("common.loading", "Loading…")}</span>
        ) : isError ? (
          <span className="text-[12px] text-risk">{t("stock.balances.error", "The balances could not be loaded.")}</span>
        ) : rows.length === 0 ? (
          <span className="text-[13px] text-ink-muted">
            {moved
              ? t("stock.none-on-hand", "None on hand: every warehouse is at 0 {{unit}}.", { unit: unitCode ?? "" })
              : t("stock.never-moved", "This part has never moved.")}
          </span>
        ) : (
          <>
            {rows.map((row) => (
              <StockFigure key={row.warehouse_id} label={row.warehouse_code} value={format(Number(row.quantity))} unit={unitCode} negative={Number(row.quantity) < 0} />
            ))}
            {rows.length > 1 && (
              <>
                <span className="h-10 w-px bg-line" aria-hidden />
                <StockFigure label={t("stock.total.label", "total")} value={format(total)} unit={unitCode} negative={total < 0} strong />
              </>
            )}
          </>
        )}
        {canWrite && (
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setAdjusting(true)}>
            <SlidersHorizontal className="size-4" />
            {t("stock.adjust.open", "Adjust stock")}
          </Button>
        )}
      </div>

      <StockMovementsGrid partId={part.id} />

      <Dialog
        open={adjusting}
        onClose={() => setAdjusting(false)}
        title={t("stock.adjust.title-for", "Adjust {{part}}", { part: part.number })}
      >
        <StockAdjustmentForm
          initialParts={[{ id: part.id, number: part.number, name: part.name, stock_unit_code: unitCode }]}
        />
      </Dialog>
    </div>
  );
}

/** One warehouse's balance, read at a glance: its code above, the quantity large. */
function StockFigure({
  label,
  value,
  unit,
  negative,
  strong = false,
}: {
  label: string;
  value: string;
  unit: string | null;
  negative: boolean;
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-24 flex-col rounded-md border px-3 py-1.5",
        negative ? "border-risk/40 bg-risk-soft" : strong ? "border-line bg-surface-sunk" : "border-line-soft bg-surface",
      )}
    >
      <span className="text-[11px] uppercase tracking-wide text-ink-muted">{label}</span>
      <span className={cn("text-[18px] leading-tight tabular-nums", strong ? "font-semibold" : "font-medium", negative ? "text-risk" : "text-ink")}>
        {value}
        {unit && <span className="ml-1 text-[12px] font-normal text-ink-muted">{unit}</span>}
      </span>
    </div>
  );
}
